import React from 'react';
import { OnboardingQuestionType } from '@core/generated/gql-types';
import {
  getDateInputBounds,
  type ChildQuestionValues,
} from '@core/util/assetMetadata.util';
import type { DocumentChildQuestion } from '@core/util/documentSlots.util';

interface DocumentChildFieldsProps {
  childQuestions: DocumentChildQuestion[];
  values: ChildQuestionValues;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
}

const fieldClassName =
  'rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 disabled:bg-gray-50 disabled:opacity-60';

const DocumentChildFields: React.FC<DocumentChildFieldsProps> = ({
  childQuestions,
  values,
  onChange,
  disabled = false,
}) => {
  if (childQuestions.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      {childQuestions.map((child) => {
        const value = values[child.key] ?? '';

        return (
          <label
            key={child.key}
            className="flex flex-col gap-1 text-sm text-gray-700"
          >
            <span className="inline-flex items-center gap-0.5 text-xs font-medium text-gray-600">
              {child.title}
              {child.required && (
                <span className="text-red-500" aria-hidden="true">
                  *
                </span>
              )}
            </span>
            {renderChildInput({
              child,
              value,
              disabled,
              onChange: (next) => onChange(child.key, next),
            })}
          </label>
        );
      })}
    </div>
  );
};

function renderChildInput({
  child,
  value,
  disabled,
  onChange,
}: {
  child: DocumentChildQuestion;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  switch (child.type) {
    case OnboardingQuestionType.Date: {
      const bounds = getDateInputBounds(child);
      return (
        <input
          type="date"
          value={value}
          min={bounds.min}
          max={bounds.max}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          className={fieldClassName}
        />
      );
    }
    case OnboardingQuestionType.MultiLineTextInput:
      return (
        <textarea
          value={value}
          placeholder={child.placeholder ?? undefined}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          rows={3}
          className={fieldClassName}
        />
      );
    case OnboardingQuestionType.Dropdown:
    case OnboardingQuestionType.SingleSelect:
    case OnboardingQuestionType.Radio: {
      const options = child.options ?? [];
      return (
        <select
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          className={fieldClassName}
        >
          <option value="">
            {child.placeholder?.trim() || `Select ${child.title.toLowerCase()}`}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }
    case OnboardingQuestionType.Email:
      return (
        <input
          type="email"
          value={value}
          placeholder={child.placeholder ?? undefined}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          className={fieldClassName}
        />
      );
    case OnboardingQuestionType.TextInput:
    default:
      return (
        <input
          type="text"
          value={value}
          placeholder={child.placeholder ?? undefined}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          className={fieldClassName}
        />
      );
  }
}

export default DocumentChildFields;
